import React, { useEffect, useRef, useState } from "react";
import "./Resume.css";
import profile from "../assets/profile.jpg";

function RevealBlock({ children, className = "" }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2
      }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`${className} ${visible ? "resume-visible" : "resume-hidden"}`}
    >
      {children}
    </div>
  );
}

export default function Resume() {
  return (
    <div className="resume-container">
      <div className="resume-wrapper">
        <RevealBlock className="resume-header-grid">
          <div className="resume-header glass-panel">
            <p className="resume-kicker">Resume</p>
            <h1 className="resume-title">Ben Booganim</h1>
            <p className="resume-subtitle">
              Software Engineering • Data Systems • Machine Learning
            </p>

            <div className="resume-contact">
              <span>425-435-4674</span>
              <span>booganimben04@gmail.com</span>

              <a
                href="http://www.linkedin.com/in/ben-booganim-1464651a0"
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn
              </a>

              <a
                href="https://github.com/BenBoog04"
                target="_blank"
                rel="noopener noreferrer"
              >
                GitHub
              </a>
            </div>

            <a
              className="resume-download"
              href="/ben_booganim_resume.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              View Resume PDF
            </a>
          </div>

          <div className="resume-photo-wrap">
            <div className="resume-photo">
              <img src={profile} alt="Ben Booganim headshot" />
            </div>
          </div>
        </RevealBlock>

        <div className="resume-grid">
          <RevealBlock className="resume-card summary-card glass-panel">
            <h2 className="section-title">Summary</h2>
            <p className="resume-text">
              Recent Arizona State University graduate currently pursuing a
              Master’s in Computer Science at Georgia Institute of Technology
              with interests in software engineering, artificial intelligence,
              and machine learning. Currently working as an AI GTM Intern at
              Nextiva, developing AI-powered automation systems and agentic
              workflows that support go-to-market operations and business
              strategy.
            </p>
          </RevealBlock>

          <RevealBlock className="resume-card glass-panel">
            <h2 className="section-title">Education</h2>

            <div className="resume-item">
              <div className="item-top">
                <h3>M.S. Computer Science</h3>
                <span>Start Date: August 2026</span>
              </div>

              <p>Georgia Institute of Technology, Atlanta, GA</p>
            </div>

            <div className="resume-item">
              <div className="item-top">
                <h3>B.S. Computer Science, Minor in Business</h3>
                <span>Graduated: May 2026</span>
              </div>

              <p>Arizona State University, Tempe, AZ</p>
              <p>GPA: 3.1 / 4.00</p>
              <p className="resume-text">
                Relevant coursework: Database Management, Foundations of
                Machine Learning, Introduction to Artificial Intelligence,
                Principles of Mobile Application Development, and Introduction
                to Natural Language Processing.
              </p>
            </div>
          </RevealBlock>

          <RevealBlock className="resume-card glass-panel">
            <h2 className="section-title">Technical Skills</h2>

            <div className="skill-group">
              <h3>Languages</h3>
              <p>Java, JavaScript, Swift, Python, C/C++, SQL</p>
            </div>

            <div className="skill-group">
              <h3>Front-End</h3>
              <p>HTML, CSS, React.js</p>
            </div>

            <div className="skill-group">
              <h3>Machine Learning</h3>
              <p>CNNs, RNNs, Deep Learning, PyTorch, NLP, GPT, RAG Pipelines</p>
            </div>

            <div className="skill-group">
              <h3>Tools, Databases, and OS</h3>
              <p>Node.js, PostgreSQL, Git, GitHub, Windows, macOS, Linux/Unix</p>
            </div>
          </RevealBlock>

          <RevealBlock className="resume-card experience-card glass-panel">
            <h2 className="section-title">Professional Experience</h2>

            <div className="experience-grid">
              <div className="resume-item">
                <div className="item-top">
                  <h3>GTM AI Intern • Nextiva</h3>
                  <span>Jun 2026 – Present</span>
                </div>

                <ul>
                  <li>Develop AI-driven workflows and automation systems using modern LLM tools and APIs to support go-to-market operations and business strategy initiatives</li>
                  <li>Integrate AI agents with platforms including Salesforce, HubSpot, Slack, Microsoft Teams, and internal data systems to streamline workflows and improve operational efficiency</li>
                  <li>Build and iterate on agentic solutions for lead qualification, opportunity surfacing, and business intelligence automation</li>
                  <li>Collaborate directly with senior leadership and cross-functional teams to prototype, deploy, and optimize AI-powered applications with measurable business impact</li>
                  <li>Utilize Python, AI frameworks, APIs, and large-scale datasets to design scalable automation and data-driven solutions in a fast-paced environment</li>
                </ul>
              </div>

              <div className="resume-item">
                <div className="item-top">
                  <h3>Finance Intern • Arizona Athletic Grounds</h3>
                  <span>Aug 2024 – Nov 2024</span>
                </div>

                <ul>
                  <li>Developed financial reports and dashboards from operational data</li>
                  <li>Used PowerQuery to automate ETL and Excel transformations</li>
                  <li>Maintained financial databases for reporting and reconciliation</li>
                  <li>Supported analysis, reporting automation, and financial modeling</li>
                </ul>
              </div>
            </div>
          </RevealBlock>

          <RevealBlock className="resume-card leadership-card glass-panel">
            <h2 className="section-title">Leadership & Activities</h2>

            <div className="resume-item">
              <div className="item-top">
                <h3>Zeta Beta Tau Fraternity</h3>
                <span>Jan 2023 – May 2026</span>
              </div>

              <ul className="leadership-list">
                <li>Served as Communications Director at the ASU chapter</li>
                <li>Served on the Supreme Council and Executive Board as Undergraduate Representative</li>
              </ul>
            </div>
          </RevealBlock>

          <RevealBlock className="resume-card projects-card glass-panel">
            <h2 className="section-title">Projects</h2>

            <div className="resume-item">
              <div className="item-top">
                <h3>ApplyTrack</h3>
                <span>Personal Project</span>
              </div>
              <ul>
                <li>Built and deployed a full-stack job application tracking platform using React, FastAPI, and MongoDB with AI-powered features including resume analysis, job posting automation, and interview preparation assistance</li>
                <li>Developed a Chrome extension to streamline job applications and designed a normalized database schema with advanced tagging, analytics, and deadline tracking capabilities</li>
                <li>Integrated Claude Sonnet AI to generate tailored resume bullets, cover letters, and interview prep based on individual job postings</li>
              </ul>
            </div>

            <div className="resume-item">
              <div className="item-top">
                <h3>Rate Your Plate</h3>
                <span>Spring 2026</span>
              </div>
              <ul>
                <li>Developed an iOS meal review app with photos, ratings, and reviews</li>
                <li>Integrated Firebase for meal review storage and retrieval</li>
                <li>Built MapKit and CoreLocation features for nearby restaurants</li>
                <li>Used SwiftUI Lists and MVVM for maintainable app structure</li>
              </ul>
            </div>

            <div className="resume-item">
              <div className="item-top">
                <h3>GM3 Solutions: Soft-Skills AI</h3>
                <span>Fall 2025 – Spring 2026</span>
              </div>
              <ul>
                <li>Built an AI-driven interview feedback platform using multimodal analysis</li>
                <li>Implemented audio, video, and text processing pipelines</li>
                <li>Integrated external AI APIs for speech recognition and feedback</li>
                <li>Developed structured scoring metrics for communication performance</li>
              </ul>
            </div>
          </RevealBlock>
        </div>
      </div>
    </div>
  );
}